import { Head } from 'vite-react-ssg';
import { Link } from "react-router-dom";
import { useState, type ReactNode } from "react";
import { ArrowRight, BookOpen, Calculator, Check, Code, ExternalLink, Gauge, Key, Layers, Brain, AlertTriangle, Terminal } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import Breadcrumbs from "@/components/Breadcrumbs";
import LastUpdated from "@/components/LastUpdated";
import { DEEPSEEK_PRICING } from "@/lib/deepseek-pricing";
import { DEEPSEEK_API } from "@/lib/deepseek-api-facts";
import { COMPETITOR_PRICING, usd } from "@/lib/competitor-pricing";

const PAGE_URL = "https://deepseek.ai/deepseek-api";
const TITLE = "DeepSeek API Guide 2026: Keys, Models, Pricing & Code";
const DESCRIPTION =
  "Independent DeepSeek API guide: get a key, call deepseek-flash or deepseek-v4-pro from Python, Node or cURL, and check current rates, limits and error codes.";

const CodeBlock = ({ code }: { code: string }) => (
  <div className="bg-gray-900 rounded-lg p-6 overflow-x-auto mt-4">
    <pre className="text-sm text-gray-100"><code>{code}</code></pre>
  </div>
);

const Inline = ({ children }: { children: ReactNode }) => (
  <code className="px-1.5 py-0.5 rounded bg-muted text-foreground text-[0.9em] [overflow-wrap:anywhere]">{children}</code>
);

const DeepseekAPI = () => {
  const api = DEEPSEEK_API;
  const flashFacts = api.models.flash;
  const proFacts = api.models.pro;
  const flash = DEEPSEEK_PRICING.models["v4-flash"];
  const pro = DEEPSEEK_PRICING.models["v4-pro"];
  const gpt = COMPETITOR_PRICING.models["gpt-5.6-terra"];
  const sonnet = COMPETITOR_PRICING.models["claude-sonnet-5"];
  const cacheSaving = (m: { inputCacheMiss: number; inputCacheHit: number }) =>
    Math.round((1 - m.inputCacheHit / m.inputCacheMiss) * 100);

  // Calculator: off-peak Flash vs two mainstream competitors, all rates from the pricing libraries.
  const [inputTokens, setInputTokens] = useState<number[]>([10]); // millions per month
  const [outputTokens, setOutputTokens] = useState<number[]>([2]);
  const deepseekCost = inputTokens[0] * flash.inputCacheMiss + outputTokens[0] * flash.output;
  const deepseekPeakCost = inputTokens[0] * flash.peak.inputCacheMiss + outputTokens[0] * flash.peak.output;
  const openaiCost = inputTokens[0] * gpt.input + outputTokens[0] * gpt.output;
  const claudeCost = inputTokens[0] * sonnet.input + outputTokens[0] * sonnet.output;

  const faqItems = [
    {
      question: "Is the DeepSeek API free?",
      answer: `No. Creating an account and an API key is free, but every call is billed per token from your account balance, so you top up first. The cheapest model, ${flashFacts.apiName}, costs ${usd(flash.inputCacheMiss)} per 1M input tokens and ${usd(flash.output)} per 1M output tokens off-peak. If DeepSeek has granted your account a promotional balance, that is used before the money you topped up. The DeepSeek chat app at chat.deepseek.com is free; the API is not.`,
    },
    {
      question: "How do I get a DeepSeek API key?",
      answer: `Sign in at platform.deepseek.com, top up your balance, open "API keys", click "Create new API key", give it a name and copy it straight away — it is shown only once. Store it in an environment variable such as DEEPSEEK_API_KEY rather than in your code.`,
    },
    {
      question: "What is the DeepSeek API base URL?",
      answer: `Use ${api.baseUrl} for the OpenAI-compatible format (chat completions live at ${api.baseUrl}${api.chatPath}) and ${api.anthropicBaseUrl} for the Anthropic-compatible format. You do not need a /v1 suffix.`,
    },
    {
      question: "Which model should I use: deepseek-flash or deepseek-v4-pro?",
      answer: `Start with ${flashFacts.apiName} (${flashFacts.version}). It is the cheapest model, supports images, and handles chat, tools and agents well. Switch individual requests to ${proFacts.apiName} (${proFacts.version}) when they need the strongest reasoning; it costs ${usd(pro.inputCacheMiss)} in / ${usd(pro.output)} out per 1M tokens off-peak. Both have a ${flashFacts.contextTokens}-token context window.`,
    },
    {
      question: "Do deepseek-chat and deepseek-reasoner still work?",
      answer: "No — do not build on them. Both were scheduled for discontinuation on 24 July 2026 and are no longer in the official model list. Replace them with deepseek-flash: thinking off for the old deepseek-chat behaviour, thinking on for deepseek-reasoner. The older deepseek-v4-flash name is still accepted for now, but it is only routed to V4.1-Flash, so switch it to deepseek-flash too.",
    },
    {
      question: "What is the DeepSeek API platform?",
      answer: "platform.deepseek.com is DeepSeek's developer console. You use it to create and revoke API keys, top up your balance, check usage and spending, and read the official API docs. It is separate from the consumer chat app at chat.deepseek.com.",
    },
    {
      question: "What are the DeepSeek API rate limits?",
      answer: `DeepSeek limits concurrent requests per account: ${flashFacts.concurrency.toLocaleString("en-US")} for ${flashFacts.apiName} and ${proFacts.concurrency} for ${proFacts.apiName}, no matter how many API keys you use. Above that you get HTTP 429. There is no published requests-per-minute or tokens-per-minute cap, and you can ask DeepSeek for a higher limit at no extra cost.`,
    },
    {
      question: "How much does the DeepSeek API cost?",
      answer: `Off-peak, ${flashFacts.apiName} costs ${usd(flash.inputCacheMiss)} per 1M input tokens (${usd(flash.inputCacheHit)} on a cache hit) and ${usd(flash.output)} per 1M output tokens; ${proFacts.apiName} costs ${usd(pro.inputCacheMiss)} / ${usd(pro.inputCacheHit)} / ${usd(pro.output)}. During peak hours (${api.peakHoursUTC}) every rate doubles. Search and Fetch cost $${api.searchFetch.pricePer1000} per 1,000 successful requests.`,
    },
    {
      question: "Is the DeepSeek API compatible with OpenAI and Anthropic SDKs?",
      answer: `Yes. Point the OpenAI SDK at ${api.baseUrl} or the Anthropic SDK at ${api.anthropicBaseUrl}, use your DeepSeek key and a DeepSeek model name. Streaming, JSON output and tool calls use the same request shape. DeepSeek-specific options such as "thinking" go in extra_body (Python) or as an extra field (Node).`,
    },
    {
      question: "Why is my DeepSeek API key not working?",
      answer: "HTTP 401 means the key is wrong or was deleted — create a new one. HTTP 402 means your balance is empty — top up. HTTP 400 or 422 usually means a malformed body or an invalid parameter, for example tool_choice: \"required\" while thinking is on. Also check that the base URL is https://api.deepseek.com and the model is deepseek-flash or deepseek-v4-pro.",
    },
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "TechArticle",
        "@id": `${PAGE_URL}#article`,
        "url": PAGE_URL,
        "headline": TITLE,
        "description": DESCRIPTION,
        "inLanguage": "en",
        "dateModified": api.lastVerified,
        "about": { "@type": "Thing", "name": "DeepSeek API" },
        "author": { "@type": "Organization", "name": "The Independent DeepSeek Guide", "url": "https://deepseek.ai/about" },
        "isPartOf": { "@type": "WebSite", "@id": "https://deepseek.ai/#website", "url": "https://deepseek.ai", "name": "The Independent DeepSeek Guide" },
        "citation": api.sources.map((s) => s.url),
      },
      {
        "@type": "HowTo",
        "name": "How to make your first DeepSeek API call",
        "step": [
          { "@type": "HowToStep", "name": "Create an account and top up", "text": "Sign in at platform.deepseek.com and add balance; calls are billed per token." },
          { "@type": "HowToStep", "name": "Create an API key", "text": "Open API keys, create a key, copy it once and store it as DEEPSEEK_API_KEY." },
          { "@type": "HowToStep", "name": "Send a request", "text": `Use the OpenAI SDK with base_url ${api.baseUrl} and model ${flashFacts.apiName}.` },
        ],
      },
      {
        "@type": "FAQPage",
        "mainEntity": faqItems.map((item) => ({
          "@type": "Question",
          "name": item.question,
          "acceptedAnswer": { "@type": "Answer", "text": item.answer },
        })),
      },
    ],
  };

  const keyGrid = [
    { label: "Base URL (OpenAI format)", value: api.baseUrl },
    { label: "Base URL (Anthropic format)", value: api.anthropicBaseUrl },
    { label: "Models", value: `${flashFacts.apiName} · ${proFacts.apiName}` },
    { label: "Context / max output", value: `${flashFacts.contextTokens} / ${flashFacts.maxOutputTokens} tokens` },
    { label: "Price from (off-peak)", value: `${usd(flash.inputCacheMiss)} in · ${usd(flash.output)} out per 1M` },
    { label: "Auth header", value: "Authorization: Bearer <key>" },
  ];

  const pythonCode = `# pip install openai
import os
from openai import OpenAI

client = OpenAI(
    api_key=os.environ["DEEPSEEK_API_KEY"],
    base_url="${api.baseUrl}",
)

response = client.chat.completions.create(
    model="${flashFacts.apiName}",
    messages=[
        {"role": "system", "content": "You are a helpful assistant."},
        {"role": "user", "content": "Explain what an API is in one sentence."},
    ],
    # Thinking is on by default. Turn it off for quick, cheap answers:
    extra_body={"thinking": {"type": "disabled"}},
)

print(response.choices[0].message.content)`;

  const nodeCode = `// npm install openai
import OpenAI from "openai";

const client = new OpenAI({
  apiKey: process.env.DEEPSEEK_API_KEY,
  baseURL: "${api.baseUrl}",
});

const res = await client.chat.completions.create({
  model: "${flashFacts.apiName}",
  messages: [
    { role: "system", content: "You are a helpful assistant." },
    { role: "user", content: "Explain what an API is in one sentence." },
  ],
  // DeepSeek-specific field; thinking is on by default
  thinking: { type: "disabled" },
});

console.log(res.choices[0].message.content);`;

  const curlCode = `curl ${api.baseUrl}${api.chatPath} \\
  -H "Content-Type: application/json" \\
  -H "Authorization: Bearer $DEEPSEEK_API_KEY" \\
  -d '{
    "model": "${flashFacts.apiName}",
    "messages": [
      {"role": "system", "content": "You are a helpful assistant."},
      {"role": "user", "content": "Explain what an API is in one sentence."}
    ],
    "thinking": {"type": "disabled"}
  }'`;

  const anthropicCode = `# pip install anthropic
import os
import anthropic

client = anthropic.Anthropic(
    api_key=os.environ["DEEPSEEK_API_KEY"],
    base_url="${api.anthropicBaseUrl}",
)

message = client.messages.create(
    model="${flashFacts.apiName}",
    max_tokens=1000,
    messages=[{"role": "user", "content": "Explain what an API is in one sentence."}],
)

print(message.content)`;

  const thinkingCode = `response = client.chat.completions.create(
    model="${proFacts.apiName}",
    messages=[{"role": "user", "content": "Is 1,001 a prime number? Explain."}],
    reasoning_effort="high",                       # "low", "high" or "max"
    extra_body={"thinking": {"type": "enabled"}},  # the default, shown for clarity
)

print(response.choices[0].message.reasoning_content)  # the chain of thought
print(response.choices[0].message.content)            # the final answer`;

  const multiTurnCode = `messages = [{"role": "user", "content": "What's the highest mountain in the world?"}]
response = client.chat.completions.create(model="${flashFacts.apiName}", messages=messages)
messages.append(response.choices[0].message)      # keep the answer

messages.append({"role": "user", "content": "And the second highest?"})
response = client.chat.completions.create(model="${flashFacts.apiName}", messages=messages)`;

  const envCode = `# macOS / Linux
export DEEPSEEK_API_KEY="sk-..."

# Windows PowerShell (current window)
$env:DEEPSEEK_API_KEY = "sk-..."

# Windows PowerShell (permanent, for new windows)
[Environment]::SetEnvironmentVariable("DEEPSEEK_API_KEY", "sk-...", "User")`;

  const claudeCodeEnv = `# macOS / Linux
export ANTHROPIC_BASE_URL=${api.anthropicBaseUrl}
export ANTHROPIC_AUTH_TOKEN=$DEEPSEEK_API_KEY
export ANTHROPIC_MODEL="${flashFacts.apiName}[1m]"
export ANTHROPIC_DEFAULT_HAIKU_MODEL=${flashFacts.apiName}

# Windows PowerShell
$env:ANTHROPIC_BASE_URL = "${api.anthropicBaseUrl}"
$env:ANTHROPIC_AUTH_TOKEN = $env:DEEPSEEK_API_KEY
$env:ANTHROPIC_MODEL = "${flashFacts.apiName}[1m]"
$env:ANTHROPIC_DEFAULT_HAIKU_MODEL = "${flashFacts.apiName}"`;

  const nextLinks = [
    { title: "DeepSeek pricing", href: "/pricing", description: "Full rate card, peak hours, price history and a cost calculator" },
    { title: "DeepSeek V4 explained", href: "/deepseek-v4", description: "What changed in the V4 generation and how the models compare" },
    { title: "Is DeepSeek safe?", href: "/is-deepseek-safe", description: "Where API data is processed and when to self-host instead" },
    { title: "Run DeepSeek locally", href: "/docs", description: "Open weights with Ollama, vLLM or llama.cpp — no API needed" },
    { title: "deepseek-chat and deepseek-reasoner retired", href: "/blog/deepseek-chat-reasoner-retired-billing-impact", description: "Migration notes for code still pinned to the old names" },
    { title: "DeepSeek vs ChatGPT", href: "/deepseek-vs-chatgpt", description: "Quality and cost compared for real workloads" },
  ];

  return (
    <>
      <Head>
        <title>{TITLE}</title>
        <meta name="description" content={DESCRIPTION} />
        <link rel="canonical" href={PAGE_URL} />
        <meta property="og:title" content={TITLE} />
        <meta property="og:description" content={DESCRIPTION} />
        <meta property="og:url" content={PAGE_URL} />
        <meta property="og:type" content="article" />
        <meta property="og:image" content="https://deepseek.ai/og-image.png" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:modified_time" content={api.lastVerified} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={TITLE} />
        <meta name="twitter:description" content={DESCRIPTION} />
        <meta name="twitter:image" content="https://deepseek.ai/og-image.png" />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Head>

      <main className="min-h-screen bg-background">
        {/* Hero: the answer first */}
        <section className="relative py-16 lg:py-20 bg-gradient-to-br from-primary/5 via-background to-primary/10">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <Breadcrumbs currentPage="DeepSeek API" />
            <div className="mt-8">
              <div className="inline-flex items-center px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-medium mb-6">
                <Code className="w-4 h-4 mr-2" />
                Independent developer guide
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-6">DeepSeek API guide</h1>
              <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mb-4">
                The DeepSeek API gives you DeepSeek&apos;s hosted models over plain HTTPS, in the same request format as the
                OpenAI and Anthropic SDKs. You change the base URL to <Inline>{api.baseUrl}</Inline>, use a DeepSeek key, and
                pick one of two models: <Inline>{flashFacts.apiName}</Inline> or <Inline>{proFacts.apiName}</Inline>. Calls
                are billed per token, from <strong className="text-foreground">{usd(flash.inputCacheMiss)} per 1M input tokens</strong>.
              </p>
              <p className="text-sm text-muted-foreground max-w-3xl mb-6">
                This page is written by an independent site, not by DeepSeek. Every model name, limit and price below is
                checked against the <a href="https://api-docs.deepseek.com/" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">official API docs</a>.
              </p>
              <LastUpdated date={api.lastVerified} className="mb-8" />

              <dl className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-8">
                {keyGrid.map((k) => (
                  <div key={k.label} className="rounded-lg border border-border bg-card px-4 py-3">
                    <dt className="text-xs text-muted-foreground">{k.label}</dt>
                    <dd className="text-sm font-semibold text-foreground break-words">{k.value}</dd>
                  </div>
                ))}
              </dl>

              <div className="flex flex-col sm:flex-row gap-4">
                <a href={api.apiKeysUrl} target="_blank" rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors">
                  <Key className="w-5 h-5 mr-2" /> Create an API key <ExternalLink className="w-4 h-4 ml-2" />
                </a>
                <a href="#first-call"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-lg border border-border bg-background text-foreground font-semibold hover:bg-muted transition-colors">
                  <Terminal className="w-5 h-5 mr-2" /> Jump to the code
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Get a key */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">How to get a DeepSeek API key</h2>
            <p className="text-muted-foreground mb-8">
              You need a DeepSeek account and a positive balance — the API has no free tier. A key takes about two minutes.
            </p>
            <ol className="space-y-6 mb-8">
              {[
                { title: "Sign in to the DeepSeek platform", text: <>Go to <a href={api.platformUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">platform.deepseek.com</a> and sign in or create an account. Trouble signing in? See our <Link to="/login" className="text-primary hover:underline">DeepSeek login help</Link>.</> },
                { title: "Top up your balance", text: "Open Top up and add credit. Without balance every call fails with HTTP 402. A promotional balance, if your account has one, is spent first." },
                { title: "Create the key", text: "Open API keys, click Create new API key, name it (for example \"production\") and copy it immediately — it is shown only once." },
                { title: "Store it outside your code", text: "Put it in an environment variable called DEEPSEEK_API_KEY. Never commit it to Git or ship it in browser or mobile code." },
              ].map((s, i) => (
                <li key={s.title} className="flex gap-4">
                  <div className="w-9 h-9 shrink-0 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold">{i + 1}</div>
                  <div>
                    <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
                    <p className="text-muted-foreground">{s.text}</p>
                  </div>
                </li>
              ))}
            </ol>
            <CodeBlock code={envCode} />
          </div>
        </section>

        {/* First call */}
        <section id="first-call" className="py-16 bg-muted/30 scroll-mt-20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Your first DeepSeek API call</h2>
            <p className="text-muted-foreground mb-2">
              Install the official OpenAI SDK (<Inline>pip install openai</Inline> or <Inline>npm install openai</Inline>), point it at
              DeepSeek and send a chat completion. No DeepSeek-specific SDK is needed.
            </p>
            <p className="text-muted-foreground mb-6">
              One thing catches most people out: <strong className="text-foreground">thinking mode is on by default</strong>. For quick
              answers, switch it off as in the examples below; you get the reply faster and pay for fewer tokens.
            </p>
            <Tabs defaultValue="python" className="w-full">
              <TabsList className="grid w-full grid-cols-4">
                <TabsTrigger value="python">Python</TabsTrigger>
                <TabsTrigger value="node">Node.js</TabsTrigger>
                <TabsTrigger value="curl">cURL</TabsTrigger>
                <TabsTrigger value="anthropic">Anthropic SDK</TabsTrigger>
              </TabsList>
              <TabsContent value="python" forceMount className="data-[state=inactive]:hidden"><CodeBlock code={pythonCode} /></TabsContent>
              <TabsContent value="node" forceMount className="data-[state=inactive]:hidden"><CodeBlock code={nodeCode} /></TabsContent>
              <TabsContent value="curl" forceMount className="data-[state=inactive]:hidden"><CodeBlock code={curlCode} /></TabsContent>
              <TabsContent value="anthropic" forceMount className="data-[state=inactive]:hidden"><CodeBlock code={anthropicCode} /></TabsContent>
            </Tabs>
            <h3 className="text-xl font-semibold text-foreground mt-10 mb-2">Keep the conversation yourself</h3>
            <p className="text-muted-foreground">
              The API is stateless: DeepSeek does not remember earlier messages. For a multi-turn chat, append each answer to
              your message list and send the whole list again.
            </p>
            <CodeBlock code={multiTurnCode} />
          </div>
        </section>

        {/* Models */}
        <section className="py-16 bg-background">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-3"><Layers className="w-7 h-7 text-primary" />DeepSeek API models</h2>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              There are two current model names. Each name always points at DeepSeek&apos;s latest release of that model, so
              results can shift slightly when DeepSeek ships an update.
            </p>
            <div className="overflow-x-auto mb-10">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-3 pr-4 font-semibold text-foreground">Model name</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Serves</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Context</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Max output</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Images</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Off-peak price (in / out per 1M)</th>
                    <th className="py-3 font-semibold text-foreground">Use it for</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { f: flashFacts, p: flash },
                    { f: proFacts, p: pro },
                  ].map(({ f, p }) => (
                    <tr key={f.apiName} className="border-b border-border align-top">
                      <td className="py-3 pr-4 font-mono text-primary font-semibold whitespace-nowrap">{f.apiName}</td>
                      <td className="py-3 pr-4 text-muted-foreground">{f.version}</td>
                      <td className="py-3 pr-4">{f.contextTokens}</td>
                      <td className="py-3 pr-4">{f.maxOutputTokens}</td>
                      <td className="py-3 pr-4">{f.vision ? "Yes" : "No"}</td>
                      <td className="py-3 pr-4">{usd(p.inputCacheMiss)} / {usd(p.output)}</td>
                      <td className="py-3 text-muted-foreground">{f.bestFor}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-muted-foreground mb-8">
              Both models support thinking and non-thinking mode, JSON output, tool calls, streaming, the OpenAI Responses API
              and the Anthropic API format. FIM (fill-in-the-middle) completion and chat prefix completion are in beta and FIM
              only works with thinking off.
            </p>

            <h3 className="text-xl font-semibold text-foreground mb-4">Old model names: what still works</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {api.legacyNames.map((l) => (
                <div key={l.names.join()} className="rounded-xl border border-border bg-card p-5">
                  <div className="font-mono text-sm text-foreground mb-2">{l.names.join(" · ")}</div>
                  <p className="text-sm text-muted-foreground mb-2">{l.status}</p>
                  <p className="text-sm"><span className="text-muted-foreground">Use instead:</span> <span className="font-mono text-primary">{l.replaceWith}</span></p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Thinking mode */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-3"><Brain className="w-7 h-7 text-primary" />Thinking mode</h2>
            <p className="text-muted-foreground mb-4">
              In thinking mode the model works through the problem before it answers. That makes it better at maths, code and
              multi-step tasks, and slower and more token-hungry on simple ones. It is <strong className="text-foreground">on by default</strong> for both models.
            </p>
            <ul className="space-y-2 mb-6 text-muted-foreground">
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Switch it with <Inline>{'"thinking": {"type": "enabled"}'}</Inline> or <Inline>{'"disabled"'}</Inline>. In the OpenAI Python SDK this goes in <Inline>extra_body</Inline>.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Set the effort with <Inline>reasoning_effort</Inline>: <Inline>low</Inline>, <Inline>high</Inline> (default) or <Inline>max</Inline>.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>The reasoning comes back in <Inline>reasoning_content</Inline>, next to the normal <Inline>content</Inline>. Its size is reported in <Inline>usage.completion_tokens_details.reasoning_tokens</Inline>.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Default <Inline>max_tokens</Inline>: {api.thinking.defaultMaxTokens.nonThinking} with thinking off, {api.thinking.defaultMaxTokens.thinking} with thinking on, {api.thinking.defaultMaxTokens.thinkingMax} at effort <Inline>max</Inline>.</span></li>
              <li className="flex gap-2"><AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" /><span><Inline>temperature</Inline> has no effect in thinking mode, and <Inline>{'tool_choice: "required"'}</Inline> returns HTTP 400 — switch thinking off for those requests.</span></li>
              <li className="flex gap-2"><AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" /><span>When you use tools in thinking mode, send <Inline>reasoning_content</Inline> back unchanged in later requests, or the API returns HTTP 400. Without tools it is ignored, so you can leave it out.</span></li>
            </ul>
            <CodeBlock code={thinkingCode} />
          </div>
        </section>

        {/* Pricing */}
        <section className="py-16 bg-background">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">DeepSeek API pricing</h2>
            <p className="text-muted-foreground mb-8 max-w-3xl">
              You pay per 1M tokens, with a cheaper rate for input that hits DeepSeek&apos;s context cache. Rates double during
              peak hours: {api.peakHoursUTC}. Everything else, including weekends, is off-peak.
            </p>
            <div className="overflow-x-auto mb-6">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-3 pr-4 font-semibold text-foreground">Per 1M tokens</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">{flashFacts.apiName} off-peak</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">{flashFacts.apiName} peak</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">{proFacts.apiName} off-peak</th>
                    <th className="py-3 font-semibold text-foreground">{proFacts.apiName} peak</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { label: "Input, cache hit", k: "inputCacheHit" as const },
                    { label: "Input, cache miss", k: "inputCacheMiss" as const },
                    { label: "Output", k: "output" as const },
                  ].map((r) => (
                    <tr key={r.k} className="border-b border-border">
                      <td className="py-3 pr-4 text-muted-foreground">{r.label}</td>
                      <td className="py-3 pr-4 font-semibold text-primary">{usd(flash[r.k])}</td>
                      <td className="py-3 pr-4">{usd(flash.peak[r.k])}</td>
                      <td className="py-3 pr-4 font-semibold text-primary">{usd(pro[r.k])}</td>
                      <td className="py-3">{usd(pro.peak[r.k])}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <ul className="space-y-2 text-muted-foreground mb-10">
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>A cache hit makes input about {cacheSaving(flash)}% cheaper on {flashFacts.apiName} and {cacheSaving(pro)}% on {proFacts.apiName}. Put the long, unchanging part of your prompt (system prompt, documents) first so repeated calls hit the cache; <Inline>usage.prompt_cache_hit_tokens</Inline> shows how much did.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Search and Fetch (live web results for agents) cost ${api.searchFetch.pricePer1000} per 1,000 successful requests.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Rates checked against the official rate card; full history and more examples on our <Link to="/pricing" className="text-primary hover:underline">DeepSeek pricing page</Link>.</span></li>
            </ul>

            <div className="bg-card border border-border rounded-2xl p-6 md:p-10 space-y-8">
              <div className="flex items-center gap-2 text-primary font-medium"><Calculator className="w-5 h-5" /> Monthly cost calculator</div>
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">Input tokens per month</label>
                  <span className="text-sm font-semibold text-primary">{inputTokens[0]}M tokens</span>
                </div>
                <Slider value={inputTokens} onValueChange={setInputTokens} min={1} max={500} step={1} />
              </div>
              <div>
                <div className="flex justify-between mb-3">
                  <label className="text-sm font-medium text-foreground">Output tokens per month</label>
                  <span className="text-sm font-semibold text-primary">{outputTokens[0]}M tokens</span>
                </div>
                <Slider value={outputTokens} onValueChange={setOutputTokens} min={1} max={500} step={1} />
              </div>
              <div className="grid md:grid-cols-3 gap-4 pt-4 border-t border-border">
                <div className="p-4 rounded-lg bg-primary/10 border-2 border-primary">
                  <div className="text-xs text-muted-foreground mb-1">{flashFacts.apiName}</div>
                  <div className="text-2xl font-bold text-primary">${deepseekCost.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground mt-1">all peak: ${deepseekPeakCost.toFixed(2)}</div>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <div className="text-xs text-muted-foreground mb-1">{gpt.label}</div>
                  <div className="text-2xl font-bold text-foreground">${openaiCost.toFixed(2)}</div>
                </div>
                <div className="p-4 rounded-lg bg-card border border-border">
                  <div className="text-xs text-muted-foreground mb-1">{sonnet.label}</div>
                  <div className="text-2xl font-bold text-foreground">${claudeCost.toFixed(2)}</div>
                </div>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                Cache-miss input rates, no cache hits counted. DeepSeek rates verified {DEEPSEEK_PRICING.lastVerified}; competitor
                list prices verified {COMPETITOR_PRICING.lastVerified}.
              </p>
            </div>
          </div>
        </section>

        {/* Limits and errors */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4 flex items-center gap-3"><Gauge className="w-7 h-7 text-primary" />Rate limits and concurrency</h2>
            <ul className="space-y-2 text-muted-foreground mb-12 max-w-4xl">
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>DeepSeek limits <em>concurrent</em> requests per account: {flashFacts.concurrency.toLocaleString("en-US")} for {flashFacts.apiName}, {proFacts.concurrency} for {proFacts.apiName}. Extra API keys do not raise it. Above the limit you get HTTP 429.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>There is no published requests-per-minute or tokens-per-minute limit. You can request a higher concurrency limit from DeepSeek at no extra cost.</span></li>
              <li className="flex gap-2"><Check className="w-5 h-5 text-primary shrink-0" /><span>Pass a <Inline>user_id</Inline> per end user (letters, digits, <Inline>_</Inline>, <Inline>-</Inline>; no personal data) to keep their cache and scheduling separate.</span></li>
              <li className="flex gap-2"><AlertTriangle className="w-5 h-5 text-amber-500 shrink-0" /><span>Under load the server sends empty lines (or <Inline>: keep-alive</Inline> comments when streaming) while you wait. Official SDKs ignore them; a custom HTTP parser must too. If inference has not started after 10 minutes, the connection is closed.</span></li>
            </ul>

            <h2 className="text-3xl font-bold text-foreground mb-6">DeepSeek API error codes and fixes</h2>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr className="border-b border-border text-left">
                    <th className="py-3 pr-4 font-semibold text-foreground">Code</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Meaning</th>
                    <th className="py-3 pr-4 font-semibold text-foreground">Usual cause</th>
                    <th className="py-3 font-semibold text-foreground">Fix</th>
                  </tr>
                </thead>
                <tbody>
                  {api.errors.map((e) => (
                    <tr key={e.code} className="border-b border-border align-top">
                      <td className="py-3 pr-4 font-mono font-semibold text-primary">{e.code}</td>
                      <td className="py-3 pr-4 text-foreground">{e.name}</td>
                      <td className="py-3 pr-4 text-muted-foreground">{e.cause}</td>
                      <td className="py-3 text-muted-foreground">{e.fix}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-sm text-muted-foreground mt-4">
              Retry 429, 500 and 503 with exponential back-off. Do not retry 400, 401, 402 or 422 — they fail the same way until you fix the request or the account.
              Outages are posted on <a href={api.statusUrl} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">status.deepseek.com</a>.
            </p>
          </div>
        </section>

        {/* Agents */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-4">Use the DeepSeek API in Claude Code, Codex and other agents</h2>
            <p className="text-muted-foreground mb-4">
              Because DeepSeek also speaks the Anthropic and OpenAI Responses formats, coding agents can run on it with a few
              environment variables. For Claude Code, point the Anthropic base URL at DeepSeek and pick the model
              (<Inline>[1m]</Inline> selects the 1M-token context):
            </p>
            <CodeBlock code={claudeCodeEnv} />
            <p className="text-muted-foreground mt-4">
              Any request for a <Inline>claude-opus*</Inline> model runs on {proFacts.apiName} and is billed at the Pro rate; other{" "}
              <Inline>claude*</Inline> names run on {flashFacts.apiName}. Some Anthropic features, such as <Inline>cache_control</Inline>,
              citations and MCP servers inside the request, are ignored. DeepSeek&apos;s{" "}
              <a href="https://api-docs.deepseek.com/quick_start/agent_integrations/claude_code" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Claude Code guide</a>{" "}
              also sets the sub-agent model and effort level, and there are guides for Codex, OpenCode and others;
              its own open-source agent is covered in our{" "}
              <Link to="/blog/deepseek-harness-open-source-claude-code-alternative" className="text-primary hover:underline">DeepSeek Harness review</Link>.
            </p>
          </div>
        </section>

        {/* Migration */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-6">Moving from OpenAI or an older DeepSeek integration</h2>
            <ol className="space-y-3 text-muted-foreground list-decimal pl-5">
              <li>Set the base URL to <Inline>{api.baseUrl}</Inline> and use your DeepSeek key.</li>
              <li>Change the model to <Inline>{flashFacts.apiName}</Inline>. Replace <Inline>deepseek-chat</Inline>, <Inline>deepseek-reasoner</Inline> and <Inline>deepseek-v4-flash</Inline> while you are at it.</li>
              <li>Decide on thinking per request: off for chat and extraction, on for reasoning-heavy work.</li>
              <li>Drop <Inline>frequency_penalty</Inline> and <Inline>presence_penalty</Inline> — DeepSeek ignores them.</li>
              <li>For JSON, set <Inline>{'response_format: {"type": "json_object"}'}</Inline> <em>and</em> ask for JSON in the prompt; the API requires both.</li>
              <li>Add back-off for HTTP 429, and alerts on HTTP 402 so an empty balance does not take your app down.</li>
            </ol>
            <p className="text-sm text-muted-foreground mt-6">
              Sending user data to a hosted API? Read <Link to="/is-deepseek-safe" className="text-primary hover:underline">where DeepSeek processes API data</Link> first.
              If that is a blocker, the open weights can run on your own hardware — see <Link to="/docs" className="text-primary hover:underline">running DeepSeek locally</Link>.
            </p>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 bg-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-foreground mb-8">DeepSeek API: frequently asked questions</h2>
            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem key={item.question} value={`item-${index}`} className="bg-card border border-border rounded-lg px-6">
                  <AccordionTrigger className="text-foreground font-semibold text-left hover:no-underline">{item.question}</AccordionTrigger>
                  <AccordionContent className="text-muted-foreground">{item.answer}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </section>

        {/* Sources + next */}
        <section className="py-16 bg-muted/30">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-3 gap-10">
            <div>
              <h2 className="text-xl font-semibold text-foreground mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5 text-primary" />Sources</h2>
              <ul className="space-y-2 text-sm">
                {api.sources.map((s) => (
                  <li key={s.url}>
                    <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline inline-flex items-center gap-1">
                      {s.title} <ExternalLink className="w-3 h-3" />
                    </a>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground mt-4">Official DeepSeek API documentation, checked {api.lastVerified}.</p>
            </div>
            <div className="md:col-span-2">
              <h2 className="text-xl font-semibold text-foreground mb-4">Keep going</h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {nextLinks.map((l) => (
                  <Link key={l.href} to={l.href} className="block rounded-xl border border-border bg-card p-4 hover:shadow-md transition-shadow">
                    <div className="font-semibold text-foreground flex items-center gap-1">{l.title} <ArrowRight className="w-4 h-4" /></div>
                    <div className="text-sm text-muted-foreground">{l.description}</div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      </main>
    </>
  );
};

export default DeepseekAPI;
